import { useTheme } from '@emotion/react';
import { arrayOf, func, number, shape, string, bool } from 'prop-types';
import { useContext } from 'react';
import Card from '../../shared/Card';
import Box from '../../shared/layouts/Box';
import Stack from '../../shared/layouts/Stack';
import { useMediaQuery } from '@dnhb/node_package';
import MessagesTable from './MessagesTable';
import Flex from '../../shared/layouts/Flex';
import OpenSvg from 'imagesUwKluis/svg/open.svg';
import PaperClip from 'imagesUwKluis/svg/paperclip.svg';
import {formatISODate} from "../../../utils/dateHelpers";
import WhiteLabelContext from "../../../context/WhiteLabelContext";
import {useTranslation} from "react-i18next";

const MessagesSection = ({ messages, maxCount, handleRowClick, isLoading }) => {
  const { breakpoints } = useTheme();
  const isMinDesktop = useMediaQuery(
    `(min-width: ${breakpoints.ml ?? '56.25rem'})`
  );
  const { isRedLabel } = useContext(WhiteLabelContext);
  const { t } = useTranslation();

  const maxCountPerView = isMinDesktop ? maxCount : Math.floor(maxCount / 2);
  const messagesToShow = maxCount ? messages.slice(0, maxCountPerView) : messages;
  const placeHolderText = isLoading ? t('Bezig met laden') : t('Je hebt nog geen berichten');

  if (isMinDesktop) {
    return (
      <>
        <Card>
          <Stack space="l">
            <MessagesTable
              messages={messagesToShow}
              handleRowClick={handleRowClick}
            />
            {messages.length === 0 && (
              <Box width="100%" color="secondaryFont" textAlign="center">
                {placeHolderText}
              </Box>
            )}
          </Stack>
        </Card>
      </>
    );
  }

  if (messages.length === 0) {
    return (
      <Card>
        <Box width="100%" color="secondaryFont" textAlign="center">{placeHolderText}</Box>
      </Card>
    );
  }

  return (
    <Card p="m" css={{ '> * + *': { marginTop: '8px' } }}>
      {messagesToShow.map(
        (
          {
            subject,
            organization_message_uuid,
            organization_name,
            attachments,
            read_at,
            created_at
          },
          i
        ) => (
          <Flex
            key={i}
            onClick={() =>
              handleRowClick({ original: { organization_message_uuid } })
            }
            alignItems="center"
            pb="s"
            width="100%"
            css={theme => ({ borderBottom: theme?.borders?.default })}
            fontWeight={!read_at ? 'bold' : null}
          >
            <Stack space="m" width="100%">
              <Flex
                fontSize={'14px'}
                lineHeight={'16px'}
                mt={0}
                minHeight="24px"
                alignItems="center"
              >
                {subject}
                {attachments?.length > 0 && (
                  <PaperClip
                    height="18px"
                    css={theme => ({
                      fill: theme?.colors?.primary || 'black',
                      margin: '0 8px',
                      flexShrink: 0
                    })}
                  />
                )}
              </Flex>
              {isRedLabel && (
                <Box fontSize={'14px'} lineHeight={'16px'} mt={0}>
                  {organization_name}
                </Box>
              )}
              <Box mt={0} fontSize="10px" lineHeight="16px">
                {formatISODate(created_at)}
              </Box>
            </Stack>
            <OpenSvg
              css={theme => ({ fill: theme?.colors?.primary || 'gray' })}
              width="1.5rem"
              height="1.5rem"
            />
          </Flex>
        )
      )}
    </Card>
  );
};

MessagesSection.propTypes = {
  messages: arrayOf(
    shape({
      attachments: arrayOf(
        shape({
          download_url: string,
          file_name: string
        })
      ),
      created_at: string,
      message: string,
      organization_name: string,
      read_at: string,
      subject: string
    })
  ),
  maxCount: number,
  handleRowClick: func,
  isLoading: bool,
};

MessagesSection.defaultProps = {
  messages: [],
  maxCount: null,
  handleRowClick: () => {},
  isLoading: false
};

export default MessagesSection;
